import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  HStack,
  VStack,
  Badge,
  Divider
} from '@chakra-ui/react';
import { getSupabase } from '../components/supabaseClient';
import { tableName, prompts } from '../components/env';
import { normalizeRole } from '../config/roles';

const SavedTranscriptCredits = () => {
  const [userCredits, setUserCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    loadSavedTranscriptCredits();
  }, []);

  const loadSavedTranscriptCredits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const supabase = await getSupabase();
      
      // console.log('📊 Loading saved transcript credits from table:', tableName);
      
      // Get all patient records with transcripts
      // Use dynamic column names from prompts configuration
      const patientNameColumn = prompts[0][0]; // First prompt field is the patient name
      const emailColumn = prompts.find(p => p[1].toLowerCase().includes('email'))?.[0] || 'email';
      
      const { data: patientData, error: patientError } = await supabase
        .from(tableName)
        .select(`
          id,
          user_id,
          ${patientNameColumn},
          ${emailColumn},
          transcripts
        `)
        .not('transcripts', 'is', null);

      if (patientError) {
        // console.error('Error loading patient data:', patientError);
        throw patientError;
      }

      // console.log('📊 Found patient records with transcripts:', patientData?.length || 0);

      // Get all users to map user_id to email
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, role');

      if (usersError) {
        // console.error('Error loading users:', usersError);
        throw usersError;
      }

      // Create a map of user_id to user info
      const userMap = {};
      usersData?.forEach(user => {
        userMap[user.id] = user;
      });

      // console.log('📊 User map created:', Object.keys(userMap).length, 'users');

      // Process transcripts and calculate credits per user
      const userCreditMap = {};
      
      patientData?.forEach(patient => {
        if (!patient.transcripts) return;
        
        const userId = patient.user_id;
        if (!userId) return;
        
        const user = userMap[userId];
        if (!user) return;
        
        // Initialize user credit data if not exists
        if (!userCreditMap[userId]) {
          userCreditMap[userId] = {
            user_id: userId,
            email: user.email,
            role: user.role,
            total_credits: 0,
            transcript_count: 0,
            patient_count: 0,
            patients: []
          };
        }
        
        // Process transcripts array
        let transcripts = [];
        if (Array.isArray(patient.transcripts)) {
          transcripts = patient.transcripts;
        } else if (patient.transcripts && typeof patient.transcripts === 'object') {
          // Single transcript object, convert to array
          transcripts = [patient.transcripts];
        }
        
        let patientCredits = 0;
        let patientTranscriptCount = 0;
        
        transcripts.forEach(transcript => {
          if (transcript.estimated_credit_usage && !isNaN(parseFloat(transcript.estimated_credit_usage))) {
            const credits = parseFloat(transcript.estimated_credit_usage);
            patientCredits += credits;
            patientTranscriptCount++;
          }
        });
        
        if (patientCredits > 0) {
          userCreditMap[userId].total_credits += patientCredits;
          userCreditMap[userId].transcript_count += patientTranscriptCount;
          userCreditMap[userId].patient_count += 1;
          userCreditMap[userId].patients.push({
            patient_id: patient.id,
            patient_name: patient[patientNameColumn] || patient[emailColumn] || 'Unknown',
            credits: patientCredits,
            transcript_count: patientTranscriptCount
          });
        }
      });

      // Convert to array and sort by total credits (descending)
      const userCreditsArray = Object.values(userCreditMap)
        .sort((a, b) => b.total_credits - a.total_credits);

      // console.log('📊 Processed user credits:', userCreditsArray.length, 'users with saved transcripts');
      // console.log('📊 User credits data:', userCreditsArray);

      setUserCredits(userCreditsArray);
      
    } catch (err) {
      // console.error('Error loading saved transcript credits:', err);
      setError('Failed to load saved transcript credits');
      toast({
        title: 'Error',
        description: 'Failed to load saved transcript credits',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getCreditsColor = (credits) => {
    if (credits >= 10) return 'red';
    if (credits >= 5) return 'orange';
    if (credits >= 2) return 'yellow';
    return 'green';
  };

  const formatCredits = (credits) => {
    return parseFloat(credits).toFixed(4);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" />
        <Text mt={4}>Loading saved transcript credits...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Saved Transcript Credits</Heading>
      
      {/* Info Alert */}
      <Alert status="info" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Saved Transcript Credits</AlertTitle>
          <AlertDescription>
            This shows credits used from saved transcripts in the {tableName} table. 
            Credits are extracted from the estimated_credit_usage field in each transcript.
          </AlertDescription>
        </Box>
      </Alert>
      
      <VStack spacing={4} align="stretch" mb={6}>
        <Text fontSize="sm" color="gray.600">
          Credits calculated from saved transcripts in patient records. This data is extracted from the transcripts JSONB array.
        </Text>
        
        <HStack spacing={3} alignSelf="flex-start">
          <Button onClick={loadSavedTranscriptCredits} size="sm">
            Refresh Data
          </Button>
        </HStack>
      </VStack>

      {userCredits.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>No Saved Transcripts Found</AlertTitle>
          <AlertDescription>
            No saved transcripts with credit usage found in the {tableName} table.
          </AlertDescription>
        </Alert>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>User Email</Th>
                <Th>Role</Th>
                <Th>Total Credits Used</Th>
                <Th>Transcript Count</Th>
                <Th>Patient Count</Th>
                <Th>Average per Transcript</Th>
              </Tr>
            </Thead>
            <Tbody>
              {userCredits.map((user) => (
                <Tr key={user.user_id}>
                  <Td fontSize="sm">{user.email}</Td>
                  <Td>
                    <Badge colorScheme={normalizeRole(user.role) === 'admin' ? 'purple' : 'blue'}>
                      {user.role}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={getCreditsColor(user.total_credits)}>
                      {formatCredits(user.total_credits)}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{user.transcript_count}</Td>
                  <Td fontSize="sm">{user.patient_count}</Td>
                  <Td fontSize="sm">
                    {user.transcript_count > 0 ? formatCredits(user.total_credits / user.transcript_count) : '0.0000'}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Text fontSize="sm" color="gray.600" mt={4}>
        Showing {userCredits.length} users with saved transcript credits
      </Text>

      {/* Detailed breakdown for users with credits */}
      {userCredits.length > 0 && (
        <Box mt={8}>
          <Heading size="md" mb={4}>Detailed Breakdown</Heading>
          {userCredits.map((user) => (
            <Box key={user.user_id} mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">{user.email}</Text>
                  <Badge colorScheme={getCreditsColor(user.total_credits)}>
                    Total: {formatCredits(user.total_credits)} credits
                  </Badge>
                </HStack>
                
                <Divider />
                
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Patients with saved transcripts:
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {user.patients.map((patient) => (
                      <HStack key={patient.patient_id} justify="space-between" fontSize="sm">
                        <Text>{patient.patient_name}</Text>
                        <HStack spacing={2}>
                          <Text color="gray.500">{patient.transcript_count} transcripts</Text>
                          <Badge size="sm" colorScheme="blue">
                            {formatCredits(patient.credits)} credits
                          </Badge>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SavedTranscriptCredits;
